<template>
    <div>
        <div class="row">
            <div class="col-md-12">
                <h2>{{ $t(componentTitleLang) }}</h2>
            </div>
        </div>
        <div class="row">
            <div class="col-md-12 mb-2" :class="componentClass">
                <component
                    :ref="optionComponent"
                    :is="optionComponent"
                    :form-errors="formErrors"
                    :date-picker-options="datePickerPreset"
                    :valid-from-notice="validFromNotice"
                    :valid-to="splitDateTime(validTo)"
                    :valid-from="splitDateTime(validFrom)"
                    :raw-valid-from="validFrom"
                    :options="componentOptions"
                    :selected-option="selectedOption"
                    :default-selected-option="defaultSelectedOption"
                    :toll-products="tollProducts"
                    :show-date-picker="showDatePicker"
                    :set-valid-from-null="setValidFromNull"
                    @update-date-picker="setDatePicker"
                    @set-invalid-valid-from-message="setInvalidValidFromMessage($event)"
                    v-bind="$attrs"
                    @product-option-selected="onProductOptionSelected($event)"
                ></component>
            </div>
        </div>
    </div>
</template>

<script>
import ProductOption from './ProductOption.vue';
import RouteTollOption from './RouteTollOption.vue';
import VignetteOption from './VignetteOption.vue';

export default {
    components: {
        ProductOption,
        RouteTollOption,
        VignetteOption,
    },

    name: 'OrderFormOptionSelection',

    props: [
        'orderFormReversed',
        'selectedProduct',
        'selectedProductObj',
        'selectedProductType',
        'formErrors',
        'datePickerConfig',
        'validTo',
        'validFrom',
        'routeTollOptions',
        'vignetteOptions',
        'selectedOption',
        'defaultSelectedOption',
        'vignetteProducts',
        'tollProducts',
        'setValidFromNull',
    ],

    data() {
        return {
            productTypeEnum: Object.freeze({
                VIGNETTE: 'vignette',
                ROUTE_TOLL: 'route_toll',
            }),
            validityNotice: null,
            validFromNotice: null,
            datePickerPreset: this.$datePickerOptions,
            productOptionSelected: false,
        };
    },

    watch: {
        selectedOption() {
            this.updateDatePicker();
        },

        selectedProduct() {
            this.updateDatePicker();
        },

        selectedProductType() {
            this.updateDatePicker();
        },
    },

    computed: {
        componentTitleLang() {
            let lang = 'shop.';

            if (this.orderFormReversed) {
                return lang += 'selected_product';
            }

            switch (this.selectedProductType) {
                case this.productTypeEnum.VIGNETTE:
                    return lang += 'selected_validity_vehicle';
                case this.productTypeEnum.ROUTE_TOLL:
                    return lang += 'selected_route';
            }

            throw new Error('Unknown product type.');
        },

        componentClass() {
            const ending = '-col';

            switch (this.selectedProductType) {
                case this.productTypeEnum.VIGNETTE:
                    return this.productType + ending;
                case this.productTypeEnum.ROUTE_TOLL:
                    return 'route-toll' + ending;
            }

            throw new Error('Unknown product type.');
        },

        optionComponent() {
            if (this.orderFormReversed) {
                return 'product-option';
            }

            switch (this.selectedProductType) {
                case this.productTypeEnum.VIGNETTE:
                    return 'vignette-option';
                case this.productTypeEnum.ROUTE_TOLL:
                    return 'route-toll-option';
            }

            throw new Error('Unknown product type.');
        },

        componentOptions() {
            if (this.orderFormReversed) {
                return this.vignetteProducts;
            }

            switch (this.selectedProductType) {
                case this.productTypeEnum.VIGNETTE:
                    return this.vignetteOptions;
                case this.productTypeEnum.ROUTE_TOLL:
                    return this.routeTollOptions;
            }

            throw new Error('Unknown product type.');
        },

        productType() {
            return this.selectedProductType;
        },

        productIsPreorder() {
            return this.selectedProductObj !== null &&
                typeof this.selectedProductObj.is_preorder === 'boolean' &&
                this.selectedProductObj.is_preorder &&
                typeof this.selectedProductObj.preorder_validity_from === 'string' &&
                typeof this.selectedProductObj.preorder_validity_until === 'string' &&
                this.selectedProductObj.preorder_validity_from.length &&
                this.selectedProductObj.preorder_validity_until.length;
        },

        shouldUpdateDatePicker() {
            return !(this.orderFormReversed && !this.productOptionSelected);
        },

        showDatePicker() {
            if (this.orderFormReversed) {
                return !!this.selectedProductObj?.product?.is_validity_controllable;
            }

            return !!this.selectedProductObj?.is_validity_controllable;
        },
    },

    methods: {
        setInvalidValidFromMessage($event) {
            this.validFromNotice = this.$t('shop.selectedInvalidDateNotice', $event);
        },

        onProductOptionSelected(args) {
            this.productOptionSelected = true;
            this.$emit('product-option-selected', args);
            this.updateDatePicker();
        },

        splitDateTime(datetime) {
            let date = '',
                time = '',
                pos = -1;

            if (typeof datetime === 'string') {
                datetime = datetime.trim();
                pos = datetime.indexOf(' ');
                date = datetime;

                if (pos > 0) {
                    date = datetime.substr(0, pos);
                    time = datetime.substr(pos + 1);
                }
            }

            return {
                date: date,
                time: time,
            };
        },

        updateDatePicker() {
            this.setDatePicker();
        },

        setDatePicker() {
            if (!this.shouldUpdateDatePicker || this.selectedProductObj === null) {
                this.datePickerPreset['minDate'] = this.datePickerConfig['minDate'];
                this.datePickerPreset['maxDate'] = this.datePickerConfig['maxDate'];

                return;
            }

            if (this.productIsPreorder) {
                this.datePickerPreset['minDate'] = this.selectedProductObj.preorder_validity_from;
                this.datePickerPreset['maxDate'] = this.datePickerConfig['maxDate'];
            } else {
                this.datePickerPreset['minDate'] = this.getDatePickerMinDate();
                this.datePickerPreset['maxDate'] = this.getDatePickerMaxDate();
            }

            const datePicker = this.$refs[this.optionComponent]?.$refs['validFrom'];
            if (datePicker) {
                datePicker.setMinDate(this.datePickerPreset['minDate']);
                datePicker.setMaxDate(this.datePickerPreset['maxDate']);
            }
        },

        getDatePickerMinDate() {
            let minDate = this.getSelectedOptionMinDate();

            if (!minDate) {
                if ('undefined' === typeof this.datePickerPreset.minDate) {
                    minDate = new Date();
                } else if (!(this.datePickerPreset.minDate instanceof Date)) {
                    minDate = new Date(this.datePickerPreset.minDate);
                } else {
                    minDate = new Date();
                    minDate.setDate(this.datePickerPreset.minDate.getDate());
                    minDate.setMonth(this.datePickerPreset.minDate.getMonth());
                    minDate.setFullYear(this.datePickerPreset.minDate.getFullYear());
                }
            }

            return minDate;
        },

        getDatePickerMaxDate() {
            if (
                this.datePickerConfig === undefined ||
                !this.datePickerConfig ||
                !this.datePickerConfig.maxDate ||
                this.selectedProductObj === null ||
                !this.selectedProductObj
            ) {
                return null;
            }

            let maxDate = null;
            if (this.orderFormReversed) {
                maxDate = this.selectedProductObj.valid_until;
            } else {
                const values = Object.values(this.selectedProductObj.options);
                maxDate = new Date(
                    Math.max(
                        ...values.map(element => {
                            if (this.productType === 'vignette') {
                                let vehicleType;

                                if (this.selectedOption) {
                                    vehicleType = this.selectedProductObj.options.find(
                                        option => option.id === this.selectedOption
                                    )?.vehicle_type;

                                    if (!vehicleType) {
                                        return this.selectedProductObj.options[0].valid_until ?
                                            new Date(this.selectedProductObj.options[0].valid_until) :
                                            0;
                                    }
                                } else {
                                    vehicleType = this.selectedProductObj.options[0].vehicle_type;
                                }

                                return element.valid_until && element.vehicle_type === vehicleType ?
                                    new Date(element.valid_until) :
                                    0;
                            } else {
                                return element.valid_until && element.selector === this.routeTollOptions[this.selectedOption].selector ?
                                    new Date(element.valid_until) :
                                    0;
                            }
                        })
                    )
                );
            }

            if (
                maxDate &&
                maxDate instanceof Date &&
                maxDate > new Date()
            ) {
                return maxDate.getFullYear() +
                    '-' +
                    ((maxDate.getMonth() + 1).toString().padStart(2, '0')) +
                    '-' +
                    (maxDate.getDate().toString().padStart(2, '0')) +
                    'T00:00:00.000000Z';
            }

            return this.datePickerConfig.maxDate;
        },

        getSelectedOptionMinDate() {
            // Return nothing, if fallback min date is not set.
            if (this.datePickerConfig === undefined || !this.datePickerConfig || !this.datePickerConfig.minDate) {
                return null;
            }

            const defaultMinDate = this.datePickerConfig.minDate;
            const productOption = this.selectedOption;

            if (!productOption) {
                return this.getFirstOptionMinDate(defaultMinDate);
            }

            let minDate = null;
            if (this.orderFormReversed) {
                minDate = new Date(this.selectedProductObj.valid_from);
            } else {
                // Get the very first minDate of the selected option.
                // If no option is selected, we use the first option.
                // After we got the minDate, we will check, if the returned date is valid for our purpose.
                // Otherwise, we will return either the minDate of the datepicker config or a valid first option min date.
                const values = Object.values(this.selectedProductObj.options);
                minDate = new Date(
                    Math.min(
                        ...values.filter(element => {
                            if (this.selectedProductType === 'vignette') {
                                let vehicleType;
                                if (this.selectedOption) {
                                    vehicleType = this.selectedProductObj.options.find(
                                        option => option.id === this.selectedOption
                                    )?.vehicle_type;

                                    if (!vehicleType) {
                                        return !!this.selectedProductObj.options[0].valid_until;
                                    }
                                } else {
                                    vehicleType = this.selectedProductObj.options[0].vehicle_type;
                                }
                                return element.valid_from && element.vehicle_type === vehicleType;
                            } else {
                                const selector = this.routeTollOptions[this.selectedOption].selector;
                                return element.valid_from && element.selector === selector;
                            }
                        }).map(element => {
                            return Math.floor(new Date(element.valid_from).getTime() / 1000);
                        })
                    ) * 1000
                );
            }

            if (
                !minDate ||
                !(minDate instanceof Date) ||
                new Date() > minDate
            ) {
                return this.getFirstOptionMinDate(defaultMinDate);
            }

            return minDate;
        },

        getFirstOptionMinDate(defaultMinDate) {
            if (!this.selectedProductObj || this.productType === 'route_toll') {
                return defaultMinDate;
            }

            const firstOptionMinDate = this.orderFormReversed ?
                this.selectedProductObj.valid_from :
                this.selectedProductObj.options[0].valid_from;

            if (
                !firstOptionMinDate ||
                new Date() > new Date(firstOptionMinDate)
            ) {
                return defaultMinDate;
            }
            return firstOptionMinDate;
        },
    },

    mounted() {
        this.setDatePicker();
    },
};
</script>
